import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut
} from 'firebase/auth';
import { RiMoonLine, RiSunLine } from 'react-icons/ri';
import ByopdfLoader from './ByopdfLoader';
import styles from './FirebaseAuth.module.css';

function FirebaseAuth() {
  const navigate = useNavigate();
  const { loading, currentUser, showNotification } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastEmail, setLastEmail] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);
  const [lastResendTime, setLastResendTime] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Redirect if already logged in and verified
  useEffect(() => {
    if (currentUser?.emailVerified) {
      // Came from a shared Story Time page: send them back to keep listening.
      let back = null;
      try { back = sessionStorage.getItem('storyTimeReturnTo'); sessionStorage.removeItem('storyTimeReturnTo'); } catch (e) { back = null; }
      navigate(back && /^\/s\/[\w-]+$/.test(back) ? back : '/');
    }
  }, [currentUser, navigate]);
  
  // Handle countdown for resend button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleResendVerification = async () => {
    try {
      // Check if 2 minutes (120 seconds) have passed since last resend
      const now = Date.now();
      const timeSinceLastResend = Math.floor((now - lastResendTime) / 1000);
      
      if (timeSinceLastResend < 120 && lastResendTime !== 0) {
        // If less than 2 minutes have passed, show error and set countdown
        const remainingTime = 120 - timeSinceLastResend;
        setResendCountdown(remainingTime);
        setError(`Please wait ${remainingTime} seconds before requesting another verification email.`);
        return;
      }
      
      setIsSubmitting(true);
      setError('');
      setMessage('');
      
      // Sign in to get the user object
      const userCredential = await signInWithEmailAndPassword(auth, lastEmail, password);
      
      // Send verification email
      await sendEmailVerification(userCredential.user);
      
      // Update last resend time
      setLastResendTime(Date.now());
      setResendCountdown(120); // 2 minutes in seconds
      
      // Show success message
      setMessage('✉️ Verification email resent! Please check your inbox (and spam folder) and click the verification link.');
      
      // Sign out the user
      await signOut(auth);
      
      // Clear password field
      setPassword('');
    } catch (error) {
      console.error('Error resending verification:', error);
      setError('Failed to resend verification email. Please try again or use a different email address.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      setMessage('');
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Google sign in error:', error);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setMessage('');

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Enter a valid email address first, then click "Forgot password?".');
      return;
    }

    try {
      setIsSubmitting(true);
      // Backend sends a branded email (button instead of the raw Firebase
      // action link); fall back to the Firebase default if it's unreachable.
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        await sendPasswordResetEmail(auth, email);
      }
      setMessage('If an account exists for this email, a password reset link has been sent.');
    } catch (error) {
      console.error('Password reset error:', error);

      if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Unable to send reset email right now. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const enterPasswordResetMode = () => {
    setIsSignUp(false);
    setIsPasswordResetMode(true);
    setPassword('');
    setError('');
    setMessage('');
  };

  const exitPasswordResetMode = () => {
    setIsPasswordResetMode(false);
    setError('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isPasswordResetMode) {
      await handleForgotPassword();
      return;
    }

    setError('');
    setMessage('');
    setIsSubmitting(true);
    
    try {
      if (isSignUp) {
        // Validate email
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
          setError('Please enter a valid email address');
          return;
        }
        
        // Validate password
        if (!password) {
          setError('Please enter a password');
          return;
        }
        if (password.length < 6) {
          setError('Password must be at least 6 characters long');
          return;
        }
        if (!/[A-Z]/.test(password)) {
          setError('Password must contain at least one uppercase letter');
          return;
        }
        if (!/[0-9]/.test(password)) {
          setError('Password must contain at least one number');
          return;
        }
        
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCredential.user);
        
        // Set message and switch to sign-in view
        setMessage('✉️ Email verification required: We\'ve sent a verification link to your email address. Please check your inbox (and spam folder) and click the link to verify your account before signing in.');
        setIsSignUp(false); // Switch to sign-in view
        
        // Store email for resend functionality
        setLastEmail(email);
        setShowResendButton(true);
        setLastResendTime(Date.now());
        setResendCountdown(120); // 2 minutes in seconds
        
        // Show notification using AuthContext
        showNotification('✉️ Email verification required: Please check your inbox (including spam folder) for the verification link. You must verify your email before signing in.', 'info');
        
        // Clear form fields
        setPassword('');
        
        // Sign out the user so they need to sign in after verification
        await signOut(auth);
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await userCredential.user.reload();
        
        if (!userCredential.user.emailVerified) {
          await sendEmailVerification(userCredential.user);
          setError('✉️ Email verification required: Your email is not verified. A new verification link has been sent to your inbox. Please check your email (including spam folder) and click the verification link before signing in.');
          
          // Store email for resend functionality
          setLastEmail(email);
          setShowResendButton(true);
          setLastResendTime(Date.now());
          setResendCountdown(120); // 2 minutes in seconds
          
          // Show notification using AuthContext
          showNotification('✉️ Email verification required: A new verification link has been sent to your inbox. Please check your email (including spam folder) and click the verification link before signing in.', 'warning');
          
          // Sign out the user immediately
          await signOut(auth);
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      console.error('Auth error code:', error.code);
      switch (error.code) {
        case 'auth/invalid-credential':
          setError('Invalid email or password. Please try again.');
          break;
        case 'auth/email-already-in-use':
          setError('An account with this email already exists.');
          break;
        case 'auth/weak-password':
          setError('Password must be at least 6 characters long.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled. Please contact support.');
          break;
        case 'auth/operation-not-allowed':
          setError('Email/password sign in is not enabled. Please try another method.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your internet connection.');
          break;
        default:
          setError(`Authentication error: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <ByopdfLoader fullPage label="Opening your library…" />;
  }

  return (
    <div className={styles.container}>
      <button
        onClick={toggleTheme}
        className={styles.themeToggle}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <RiSunLine size={24} /> : <RiMoonLine size={24} />}
      </button>
      
      <div className={styles.card}>
        <div className={styles.header}>
          <a href="https://byopdf.com" className={styles.logoLink} aria-label="Back to byopdf.com">
            <img
              src={isDark ? '/logo_mark_cream.png' : '/logo_mark_black.png'}
              alt=""
              aria-hidden="true"
              className={styles.logoMark}
            />
            <h1 className={styles.logo}>BYOPDF</h1>
          </a>
          <p className={styles.subtitle}>
            Build a reading habit on your own terms, one book at a time
          </p>
        </div>

        {error && (
          <div className={`${styles.alert} ${styles.error}`}>
            {error}
          </div>
        )}

        {message && (
          <div className={`${styles.alert} ${styles.message}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              disabled={isSubmitting}
              className={styles.input}
            />
          </div>
          
          {!isPasswordResetMode && (
            <div className={styles.inputGroup}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                disabled={isSubmitting}
                className={styles.input}
              />
              {isSignUp && (
                <div className={styles.passwordRequirements}>
                  Password requirements:
                  <ul>
                    <li>At least 6 characters long</li>
                    <li>Must contain at least one uppercase letter</li>
                    <li>Must contain at least one number</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {!isSignUp && (
            <div className={styles.forgotPasswordRow}>
              {isPasswordResetMode ? (
                <button
                  type="button"
                  onClick={exitPasswordResetMode}
                  disabled={isSubmitting}
                  className={styles.forgotPasswordButton}
                >
                  Back to sign in
                </button>
              ) : (
                <button
                  type="button"
                  onClick={enterPasswordResetMode}
                  disabled={isSubmitting}
                  className={styles.forgotPasswordButton}
                >
                  Forgot password?
                </button>
              )}
            </div>
          )}

          {isPasswordResetMode && (
            <p className={styles.resetHint}>
              If you created your account with Google, use the Google sign-in button instead.
            </p>
          )}
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={styles.primaryButton}
          >
            {isSubmitting ? 'Please wait...' : (isSignUp ? 'Sign Up' : isPasswordResetMode ? 'Send reset link' : 'Sign In')}
          </button>
          
          {showResendButton && !isSignUp && !isPasswordResetMode && (
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={isSubmitting || resendCountdown > 0}
              className={styles.resendButton}
            >
              {resendCountdown > 0
                ? `Resend verification email (${resendCountdown}s)`
                : 'Resend verification email'}
            </button>
          )}

          {!isPasswordResetMode && (
            <>
              <div className={styles.divider}>
                <span className={styles.dividerText}>Or continue with</span>
              </div>

              <button
                type="button"
                onClick={signInWithGoogle}
                disabled={isSubmitting}
                className={styles.googleButton}
              >
                <img 
                  src="https://www.google.com/favicon.ico" 
                  alt="Google"
                  className={styles.googleIcon}
                />
                {isSubmitting ? 'Please wait...' : 'Google'}
              </button>
            </>
          )}
          
          <div className={styles.toggleContainer}>
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setIsPasswordResetMode(false);
                setError('');
                setMessage('');
              }}
              disabled={isSubmitting}
              className={styles.toggleButton}
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </button>
          </div>

          <div className={styles.backLinkRow}>
            <a href="https://byopdf.com" className={styles.backLink}>← Back to byopdf.com</a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FirebaseAuth;
